import { getOtherColor, getPrimaryColor } from '@revelio/replots';

import {
  type BreakdownBoxDataSet,
  type BreakdownSingleDataSet,
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
  type PercentileDataPoint,
  type SingleDataPoint,
  type TableRowsDataSet,
  type TimeseriesDataSet,
} from '../../DataProvider/types';
import { getPooledPercentilesFromHistogram } from '../../DataProvider/utils/convert-compensation-data';
import {
  isParameterSelectionEqualToBoxDataSet,
  isParameterSelectionEqualToDataPoint,
  type ParameterSelection,
} from './check-datapoint-in-parameter';

type SingleDataPointWithHistogram = SingleDataPoint & {
  histogramCounts?: number[];
  bucketSize?: number;
  exchangeRate?: number;
};

function hasHistogramData(
  point: SingleDataPoint
): point is SingleDataPointWithHistogram {
  const p = point as SingleDataPointWithHistogram;
  return (
    Array.isArray(p.histogramCounts) &&
    p.histogramCounts.length > 0 &&
    typeof p.bucketSize === 'number' &&
    typeof p.exchangeRate === 'number'
  );
}

/** Sum histogram counts bucket by bucket */
function sumHistogramCounts(arrays: number[][]): number[] {
  if (arrays.length === 0) return [];
  const maxLen = Math.max(...arrays.map((a) => a.length));
  const result = new Array(maxLen).fill(0);
  for (const arr of arrays) {
    for (let i = 0; i < arr.length; i++) {
      result[i] += arr[i] ?? 0;
    }
  }
  return result;
}

const PERCENTILE_KEYS: (keyof PercentileDataPoint)[] = [
  'p10',
  'p25',
  'p50',
  'p75',
  'p90',
];

function hasPercentiles(
  point: SingleDataPoint
): point is SingleDataPoint & PercentileDataPoint {
  return PERCENTILE_KEYS.every(
    (k) =>
      typeof (point as SingleDataPoint & PercentileDataPoint)[k] === 'number'
  );
}

function averagePercentilesAcrossPoints(
  points: (SingleDataPoint & PercentileDataPoint)[]
): PercentileDataPoint | null {
  if (points.length === 0) return null;
  const result: PercentileDataPoint = {
    p10: 0,
    p25: 0,
    p50: 0,
    p75: 0,
    p90: 0,
  };
  for (const key of PERCENTILE_KEYS) {
    let sum = 0;
    let count = 0;
    for (const p of points) {
      const v = p[key];
      if (typeof v === 'number' && !Number.isNaN(v)) {
        sum += v;
        count += 1;
      }
    }
    result[key] = count > 0 ? sum / count : 0;
  }
  return result;
}

/**
 * Aggregates data for a comparison group parameter when aggregation is enabled.
 * Currently only supports AVG (average) aggregation.
 */
export const aggregateParameterData = ({
  data,
  parameterName,
  aggregationType,
  primaryFilterDimensions,
}: {
  data: DataProviderSet;
  parameterName: string;
  aggregationType: 'AVG';
  primaryFilterDimensions?: ParameterSelection[];
}): DataProviderSet => {
  if (aggregationType !== 'AVG') {
    return data;
  }

  switch (data.type) {
    case DataType.SINGLE: {
      const seriesPoints = data.data as SingleDataPoint[];

      // For single data sets, keep the primary filter value(s) and aggregate the rest
      if (seriesPoints.length <= 1) {
        return data;
      }

      // Find all primary points by matching dimension, or fall back to first point
      let primaryPoints: SingleDataPoint[];
      if (primaryFilterDimensions) {
        primaryPoints = seriesPoints.filter((point) =>
          primaryFilterDimensions.some((p) =>
            isParameterSelectionEqualToDataPoint(p, point)
          )
        );
        // If no matches found, fall back to first point
        if (primaryPoints.length === 0) {
          const firstPoint = seriesPoints[0];
          if (!firstPoint) {
            return data;
          }
          primaryPoints = [firstPoint];
        }
      } else {
        const firstPoint = seriesPoints[0];
        if (!firstPoint) {
          return data;
        }
        primaryPoints = [firstPoint];
      }

      // Filter out primary points to get the rest
      const primaryDims = new Set(primaryPoints.map((p) => p.dim));
      const restPoints = seriesPoints.filter(
        (point) => !primaryDims.has(point.dim)
      );

      // Aggregate the rest of the points by dimension
      const valueMap = new Map<string, { sum: number; count: number }>();

      restPoints.forEach((point) => {
        const existing = valueMap.get(point.dim);
        if (existing) {
          existing.sum += point.value ?? 0;
          existing.count += 1;
        } else {
          valueMap.set(point.dim, {
            sum: point.value ?? 0,
            count: 1,
          });
        }
      });

      // Create aggregated point for the parameter name
      const aggregatedPoints: SingleDataPoint[] = Array.from(
        valueMap.entries()
      ).map(([dim, { sum, count }]) => ({
        dim,
        value: count > 0 ? sum / count : null,
      }));

      // If we have aggregated values, replace them with a single aggregated dimension
      // Otherwise, just keep the primary points
      const aggregatedValue =
        aggregatedPoints.length > 0
          ? aggregatedPoints.reduce((sum, p) => sum + (p.value ?? 0), 0) /
            aggregatedPoints.length
          : null;

      // Prefer pooled percentiles over averaging percentiles
      const restWithHistogram = restPoints.filter(
        (p): p is SingleDataPointWithHistogram => hasHistogramData(p)
      );
      const firstWithHistogram = restWithHistogram[0];
      const pooledPercentiles =
        restWithHistogram.length > 0 &&
        firstWithHistogram &&
        Array.isArray(firstWithHistogram.histogramCounts)
          ? getPooledPercentilesFromHistogram(
              sumHistogramCounts(
                restWithHistogram.map((p) => p.histogramCounts ?? [])
              ),
              firstWithHistogram.bucketSize ?? 0,
              firstWithHistogram.exchangeRate ?? 1
            )
          : null;

      const restWithPercentiles = restPoints.filter(
        (p): p is SingleDataPoint & PercentileDataPoint => hasPercentiles(p)
      );
      const averagedPercentiles =
        !pooledPercentiles && restWithPercentiles.length > 0
          ? averagePercentilesAcrossPoints(restWithPercentiles)
          : null;

      const coloredPrimaryPoints = primaryPoints.map((point) => ({
        ...point,
        color: getPrimaryColor(),
      }));

      const aggregatedRow: SingleDataPoint = {
        dim: parameterName,
        value: aggregatedValue,
        color: getOtherColor(),
        ...(pooledPercentiles ?? (averagedPercentiles && averagedPercentiles)),
      };

      const aggregatedData: SingleDataPoint[] =
        aggregatedPoints.length > 0 && aggregatedValue !== null
          ? [...coloredPrimaryPoints, aggregatedRow]
          : coloredPrimaryPoints;

      return { type: DataType.SINGLE, data: aggregatedData };
    }

    case DataType.BREAKDOWN_SINGLE: {
      // For breakdown single, keep the primary group(s) and aggregate the rest
      if (data.data.length <= 1) {
        return data;
      }

      // Find all primary groups by matching dimension, or fall back to first group
      let primaryGroups: BreakdownSingleDataSet['data'];
      if (primaryFilterDimensions) {
        primaryGroups = data.data.filter((group) =>
          primaryFilterDimensions.some((p) =>
            isParameterSelectionEqualToDataPoint(p, {
              dim: group.dim,
              value: null,
              id: group.dim,
            })
          )
        );
        // If no matches found, fall back to first group
        if (primaryGroups.length === 0) {
          const firstGroup = data.data[0];
          if (!firstGroup) {
            return data;
          }
          primaryGroups = [firstGroup];
        }
      } else {
        const firstGroup = data.data[0];
        if (!firstGroup) {
          return data;
        }
        primaryGroups = [firstGroup];
      }

      // Filter out primary groups to get the rest
      const primaryDims = new Set(primaryGroups.map((g) => g.dim));
      const restGroups = data.data.filter(
        (group) => !primaryDims.has(group.dim)
      );

      // Aggregate breakdown values across all rest groups
      // For each dimension in the breakdown, average values across all rest groups
      type DimStats = {
        sum: number;
        count: number;
        shareSum: number;
        shareCount: number;
        percentileSums: PercentileDataPoint;
        percentileCounts: PercentileDataPoint;
      };
      const dimensionMap = new Map<string, DimStats>();

      const emptyPercentile = (): PercentileDataPoint => ({
        p10: 0,
        p25: 0,
        p50: 0,
        p75: 0,
        p90: 0,
      });

      restGroups.forEach((group) => {
        group.breakdown.forEach((point) => {
          const existing = dimensionMap.get(point.dim);
          const percentileSum = emptyPercentile();
          const percentileCount = emptyPercentile();
          if (hasPercentiles(point)) {
            for (const k of PERCENTILE_KEYS) {
              percentileSum[k] = point[k];
              percentileCount[k] = 1;
            }
          }
          if (existing) {
            existing.sum += point.value ?? 0;
            existing.count += 1;
            if (point.share !== null && point.share !== undefined) {
              existing.shareSum += point.share;
              existing.shareCount += 1;
            }
            if (hasPercentiles(point)) {
              for (const k of PERCENTILE_KEYS) {
                existing.percentileSums[k] += point[k];
                existing.percentileCounts[k] += 1;
              }
            }
          } else {
            dimensionMap.set(point.dim, {
              sum: point.value ?? 0,
              count: 1,
              shareSum: point.share ?? 0,
              shareCount:
                point.share !== null && point.share !== undefined ? 1 : 0,
              percentileSums: percentileSum,
              percentileCounts: percentileCount,
            });
          }
        });
      });

      const aggregatedBreakdown: SingleDataPoint[] = Array.from(
        dimensionMap.entries()
      ).map(([dim, stats]) => {
        const base: SingleDataPoint = {
          dim,
          value: stats.count > 0 ? stats.sum / stats.count : null,
          share:
            stats.shareCount > 0 ? stats.shareSum / stats.shareCount : null,
        };
        const hasAnyPercentile = PERCENTILE_KEYS.some(
          (k) => stats.percentileCounts[k] > 0
        );
        if (!hasAnyPercentile) return base;
        const percentiles: PercentileDataPoint = emptyPercentile();
        for (const k of PERCENTILE_KEYS) {
          const n = stats.percentileCounts[k];
          percentiles[k] = n > 0 ? stats.percentileSums[k] / n : 0;
        }
        return { ...base, ...percentiles };
      });

      // Return primary group(s) + aggregated group with parameter name
      const aggregatedData: BreakdownSingleDataSet = {
        type: DataType.BREAKDOWN_SINGLE,
        data: [
          ...primaryGroups,
          {
            dim: parameterName,
            breakdown: aggregatedBreakdown,
          },
        ],
      };

      return aggregatedData;
    }

    case DataType.TIMESERIES: {
      // For timeseries, keep the primary series and aggregate the rest
      if (data.data.length <= 1) {
        return data;
      }

      // Find all primary series by matching dimension, or fall back to first series
      let primarySeries: TimeseriesDataSet['data'];
      if (primaryFilterDimensions) {
        primarySeries = data.data.filter((series) =>
          primaryFilterDimensions.some((p) =>
            isParameterSelectionEqualToDataPoint(p, series)
          )
        );
        // If no matches found, fall back to first series
        if (primarySeries.length === 0) {
          const firstSeries = data.data[0];
          if (!firstSeries) {
            return data;
          }
          primarySeries = [firstSeries];
        }
      } else {
        const firstSeries = data.data[0];
        if (!firstSeries) {
          return data;
        }
        primarySeries = [firstSeries];
      }

      // Filter out primary series to get the rest
      const primaryDims = new Set(primarySeries.map((s) => s.dim));
      const restSeries = data.data.filter(
        (series) => !primaryDims.has(series.dim)
      );

      // Aggregate values for each date across all rest series
      const dateMap = new Map<
        string,
        { sum: number; count: number; shareSum: number; shareCount: number }
      >();

      restSeries.forEach((series) => {
        series.timeseries.forEach((point) => {
          const existing = dateMap.get(point.date);
          if (existing) {
            existing.sum += point.value ?? 0;
            existing.count += 1;
            if (point.share !== null && point.share !== undefined) {
              existing.shareSum += point.share;
              existing.shareCount += 1;
            }
          } else {
            dateMap.set(point.date, {
              sum: point.value ?? 0,
              count: 1,
              shareSum: point.share ?? 0,
              shareCount:
                point.share !== null && point.share !== undefined ? 1 : 0,
            });
          }
        });
      });

      const aggregatedTimeseries = Array.from(dateMap.entries())
        .map(([date, { sum, count, shareSum, shareCount }]) => ({
          date,
          value: count > 0 ? sum / count : null,
          share: shareCount > 0 ? shareSum / shareCount : null,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      const aggregatedData: TimeseriesDataSet = {
        type: DataType.TIMESERIES,
        data: [
          ...primarySeries,
          {
            dim: parameterName,
            timeseries: aggregatedTimeseries,
          },
        ],
      };

      return aggregatedData;
    }

    case DataType.BOX: {
      if (!isBoxBreakdownDataSet(data)) {
        return data;
      }
      const boxData = data.data;
      if (boxData.length <= 1) return data;

      const firstGroup = boxData[0];
      if (!firstGroup) return data;

      // With secondary dimension: top-level dim is secondary (e.g. role), breakdown holds one entry per primary (company).
      // No secondary: top-level dim is primary (company), single breakdown entry per group.
      const hasSecondaryDimension =
        firstGroup.breakdown.length > 1 ||
        firstGroup.dim !== firstGroup.breakdown[0]?.dim;

      if (hasSecondaryDimension) {
        // With secondary dimension: primary is in each group's breakdown
        const aggregatedData: BreakdownBoxDataSet['data'] = boxData.map(
          (group) => {
            const primaryBreakdown = group.breakdown.filter((b) =>
              primaryFilterDimensions?.some((p) =>
                isParameterSelectionEqualToBoxDataSet(p, b)
              )
            );
            const restBreakdown = group.breakdown.filter(
              (b) =>
                !primaryFilterDimensions?.some((p) =>
                  isParameterSelectionEqualToBoxDataSet(p, b)
                )
            );

            if (restBreakdown.length === 0) {
              return { dim: group.dim, breakdown: primaryBreakdown };
            }

            const n = restBreakdown.length;
            const withQ25 = restBreakdown.filter(
              (b) => b.box.q25 !== null && b.box.q25 !== undefined
            );
            const withQ75 = restBreakdown.filter(
              (b) => b.box.q75 !== null && b.box.q75 !== undefined
            );
            const aggregatedBox = {
              q10: restBreakdown.reduce((sum, b) => sum + b.box.q10, 0) / n,
              q50: restBreakdown.reduce((sum, b) => sum + b.box.q50, 0) / n,
              q90: restBreakdown.reduce((sum, b) => sum + b.box.q90, 0) / n,
              ...(withQ25.length > 0 && {
                q25:
                  withQ25.reduce((s, b) => s + (b.box.q25 ?? 0), 0) /
                  withQ25.length,
              }),
              ...(withQ75.length > 0 && {
                q75:
                  withQ75.reduce((s, b) => s + (b.box.q75 ?? 0), 0) /
                  withQ75.length,
              }),
            };

            return {
              dim: group.dim,
              breakdown: [
                ...primaryBreakdown,
                { dim: parameterName, box: aggregatedBox },
              ],
            };
          }
        );

        return { type: DataType.BOX, data: aggregatedData };
      }

      // No secondary dimension: primary is top-level dim (same as BREAKDOWN_SINGLE)
      let primaryGroups: BreakdownBoxDataSet['data'];
      if (primaryFilterDimensions?.length) {
        primaryGroups = boxData.filter((group) => {
          const firstBreakdown = group.breakdown[0];
          return (
            firstBreakdown &&
            primaryFilterDimensions.some((p) =>
              isParameterSelectionEqualToBoxDataSet(p, firstBreakdown)
            )
          );
        });
        if (primaryGroups.length === 0) {
          const first = boxData[0];
          if (!first) return data;
          primaryGroups = [first];
        }
      } else {
        const first = boxData[0];
        if (!first) return data;
        primaryGroups = [first];
      }

      const primaryDims = new Set(primaryGroups.map((g) => g.dim));
      const restGroups = boxData.filter((group) => !primaryDims.has(group.dim));

      if (restGroups.length === 0) {
        return data;
      }

      const allRestBoxes = restGroups.flatMap((g) => g.breakdown);
      const count = allRestBoxes.length;
      const withQ25 = allRestBoxes.filter(
        (b) => b.box.q25 !== null && b.box.q25 !== undefined
      );
      const withQ75 = allRestBoxes.filter(
        (b) => b.box.q75 !== null && b.box.q75 !== undefined
      );
      const aggregatedBox = {
        q10: allRestBoxes.reduce((sum, b) => sum + b.box.q10, 0) / count,
        q50: allRestBoxes.reduce((sum, b) => sum + b.box.q50, 0) / count,
        q90: allRestBoxes.reduce((sum, b) => sum + b.box.q90, 0) / count,
        ...(withQ25.length > 0 && {
          q25:
            withQ25.reduce((s, b) => s + (b.box.q25 ?? 0), 0) / withQ25.length,
        }),
        ...(withQ75.length > 0 && {
          q75:
            withQ75.reduce((s, b) => s + (b.box.q75 ?? 0), 0) / withQ75.length,
        }),
      };

      const aggregatedData: BreakdownBoxDataSet = {
        type: DataType.BOX,
        data: [
          ...primaryGroups,
          {
            dim: parameterName,
            breakdown: [{ dim: parameterName, box: aggregatedBox }],
          },
        ],
      };

      return aggregatedData;
    }

    case DataType.TABLE_ROWS: {
      const rows = data.data;
      if (
        rows.length <= 1 ||
        !parameterName ||
        !primaryFilterDimensions?.length
      ) {
        return data;
      }

      const primaryRows = rows.filter((row) => {
        const cell = row[parameterName];
        const cellStr =
          cell !== null && cell !== undefined ? String(cell) : undefined;
        return primaryFilterDimensions.some(
          (p) => cellStr === p.name || cellStr === p.id
        );
      });
      const restRows = rows.filter((row) => {
        const cell = row[parameterName];
        const cellStr =
          cell !== null && cell !== undefined ? String(cell) : undefined;
        return !primaryFilterDimensions.some(
          (p) => cellStr === p.name || cellStr === p.id
        );
      });

      if (restRows.length === 0) return data;

      const allKeys = new Set<string>();
      // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
      restRows.forEach((row) =>
        // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
        Object.keys(row).forEach((k) => allKeys.add(k))
      );

      const aggregatedRow: Record<string, unknown> = {};
      for (const key of allKeys) {
        if (key === parameterName) {
          aggregatedRow[key] = parameterName;
          continue;
        }
        const values = restRows
          .map((r) => r[key])
          .filter(
            (v): v is number => typeof v === 'number' && !Number.isNaN(v)
          );
        if (values.length === restRows.length && values.length > 0) {
          aggregatedRow[key] =
            values.reduce((sum, v) => sum + v, 0) / values.length;
        } else {
          const first = restRows[0]?.[key];
          aggregatedRow[key] = first !== undefined ? first : null;
        }
      }

      const aggregatedData: TableRowsDataSet = {
        type: DataType.TABLE_ROWS,
        data: [...primaryRows, aggregatedRow],
      };

      return aggregatedData;
    }

    default:
      // For other data types, return as-is (aggregation not supported yet)
      return data;
  }
};
